/// Tabbed help modal. Opens from the `?` button on the game
/// screen (with both tabs) or the home screen (Concepts only).
///
///   - Brief tab — the level's static briefing text + goal mix.
///     Same content the auto-shown LevelBrief used to render.
///     Hidden on the home screen (no level context).
///   - Concepts tab — list of small cards explaining each game
///     concept (selecting aircraft, heading/alt/speed, cleared
///     for approach, hold, time multiplier, etc.).

import { useState } from 'react'
import { CONCEPT_CARDS } from '../data/conceptCards'
import { localized, type LevelGoalsSummary, type LocalizedString } from '../types/LevelData'

export type HelpTab = 'brief' | 'concepts'

export function HelpModal(props: {
  /// Level brief text. Pass undefined when opening from the home
  /// screen — the Brief tab is hidden in that case.
  brief: LocalizedString | undefined
  goals: LevelGoalsSummary | null
  /// Tab to open with. Caller decides based on context (e.g.
  /// game screen defaults to 'brief' if a brief exists, 'concepts'
  /// otherwise; home screen always 'concepts').
  initialTab: HelpTab
  onDismiss: () => void
}) {
  const briefText = localized(props.brief)
  const hasBrief = Boolean(briefText) || (props.goals != null && props.goals.total > 0)
  const [tab, setTab] = useState<HelpTab>(
    hasBrief ? props.initialTab : 'concepts',
  )

  return (
    <div
      className="pointer-events-auto absolute inset-0 z-30 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={props.onDismiss}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[85vh] w-full max-w-md flex-col rounded-xl bg-slate-900 p-5 text-slate-100 shadow-2xl ring-1 ring-white/10"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold tracking-wide">Help</h2>
          <button
            onClick={props.onDismiss}
            className="text-xs text-slate-400 hover:text-white"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {hasBrief && (
          <div className="mt-3 flex gap-1.5">
            <TabButton
              active={tab === 'brief'}
              onClick={() => setTab('brief')}
              label="Level brief"
            />
            <TabButton
              active={tab === 'concepts'}
              onClick={() => setTab('concepts')}
              label="Concepts"
            />
          </div>
        )}

        <div className="mt-3 overflow-y-auto">
          {tab === 'brief' && hasBrief ? (
            <BriefTab brief={briefText} goals={props.goals} />
          ) : (
            <ConceptsTab />
          )}
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={props.onDismiss}
            className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-emerald-500"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  )
}

function TabButton(props: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      onClick={props.onClick}
      className={
        'flex-1 rounded-md px-2 py-1.5 text-xs font-semibold transition-colors ' +
        (props.active
          ? 'bg-emerald-600 text-white shadow ring-1 ring-emerald-400'
          : 'bg-slate-800 text-slate-300 ring-1 ring-white/10 hover:bg-slate-700')
      }
    >
      {props.label}
    </button>
  )
}

function BriefTab(props: { brief: string | undefined; goals: LevelGoalsSummary | null }) {
  return (
    <>
      {props.goals && props.goals.total > 0 && (
        <div className="rounded-lg bg-slate-800/70 px-3 py-2 text-sm text-slate-200 ring-1 ring-white/10">
          <div className="mb-1 text-xs uppercase tracking-wide text-slate-400">
            Goals
          </div>
          <div className="font-medium">{goalMixLine(props.goals)}</div>
          <div className="mt-1 text-xs text-slate-300">
            Pass: {Math.round(props.goals.requiredPct * 100)}% — handle{' '}
            <span className="font-semibold text-emerald-300">
              {props.goals.targetCompleted}
            </span>{' '}
            of {props.goals.total}.
          </div>
        </div>
      )}
      {props.brief && (
        <pre className="mt-3 whitespace-pre-wrap font-sans text-sm leading-relaxed text-slate-200">
          {props.brief}
        </pre>
      )}
    </>
  )
}

function ConceptsTab() {
  return (
    <ul className="space-y-2">
      {CONCEPT_CARDS.map((card) => (
        <li
          key={card.id}
          className="rounded-md bg-slate-800/60 px-3 py-2 ring-1 ring-white/5"
        >
          <div className="text-sm font-semibold text-slate-100">{card.title}</div>
          <p className="mt-1 text-xs leading-relaxed text-slate-300">{card.body}</p>
        </li>
      ))}
    </ul>
  )
}

function goalMixLine(g: LevelGoalsSummary): string {
  const parts: string[] = []
  if (g.landings > 0) parts.push(`${g.landings} ${g.landings === 1 ? 'arrival' : 'arrivals'}`)
  if (g.departures > 0)
    parts.push(`${g.departures} ${g.departures === 1 ? 'departure' : 'departures'}`)
  if (g.transitions > 0)
    parts.push(`${g.transitions} ${g.transitions === 1 ? 'transition' : 'transitions'}`)
  return parts.join(' · ')
}
